import React, { useState, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaQrcode, FaUserPlus, FaLink, FaCopy, FaCheckCircle } from 'react-icons/fa';
import { addClientToLocal } from '../../../../../../../../crud/clients_api/local_clients';
import { ClientLocal } from '../../../../types/client.types';
import QRCode from 'react-qr-code';
import QRScanner from '../../../../../../../../components/common/QRCodes/QRScanner';
import './AddClient.scss';

interface AddClientProps {
  show: boolean;
  handleClose: () => void;
  onSuccess: (newClient?: ClientLocal) => void;
  localId: number;
}

const AddClient: React.FC<AddClientProps> = ({
  show,
  handleClose,
  onSuccess,
  localId,
}) => {
  const [activeTab, setActiveTab] = useState<string>('code');
  const [clientCode, setClientCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrInput, setQrInput] = useState(false);

  // Generate invitation link
  const invitationLink = `${window.location.origin}/ClientPortal?local_id=${localId}`;

  // Extract client code from QR URL
  const extractClientCodeFromUrl = (input: string): string => {
    // If it's a URL with ClientPortal, extract the code parameter
    if (input.includes('client_code=')) {
      const urlParams = new URLSearchParams(input.split('?')[1]);
      return urlParams.get('client_code') || input;
    }
    // Otherwise, assume it's the code itself
    return input;
  };

  // Handle QR code scanned
  const handleQRCodeScanned = useCallback((scannedContent: string) => {
    const extractedCode = extractClientCodeFromUrl(scannedContent);
    setClientCode(extractedCode);
    setQrInput(true);
  }, []);

  const handleSubmitCode = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!clientCode.trim()) {
      toast.error('Por favor ingresa un código de cliente');
      return;
    }

    setLoading(true);

    try {
      const newClient = await addClientToLocal(clientCode.trim(), localId);
      toast.success('Cliente agregado correctamente');
      setClientCode('');
      setShowQRScanner(false);
      
      // Pass the new client data to parent so it can append to list
      onSuccess(newClient);
      handleClose();
    } catch (error: any) {
      console.error('Error adding client:', error);
      const message = error?.response?.data?.message || error?.message || 'Error al agregar el cliente';
      toast.error(message);
    } finally {
      setLoading(false);
      setQrInput(false);
    }
  }, [clientCode, localId, onSuccess, handleClose]);

  // Auto-submit when QR is scanned
  React.useEffect(() => {
    if (qrInput && clientCode) {
      handleSubmitCode();
    }
  }, [qrInput, clientCode, handleSubmitCode]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(invitationLink);
    setCopied(true);
    toast.success('Enlace copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="add-client-modal">
      <Modal.Header closeButton>
        <Modal.Title>Agregar Cliente</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="custom-tabs">
          <div className="tab-nav">
            <button
              className={`tab-button ${activeTab === 'code' ? 'active' : ''}`}
              onClick={() => setActiveTab('code')}
            >
              <FaUserPlus />
              <span>Por Código</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'qr' ? 'active' : ''}`}
              onClick={() => setActiveTab('qr')}
            >
              <FaQrcode />
              <span>Escanear QR</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'invite' ? 'active' : ''}`}
              onClick={() => setActiveTab('invite')}
            >
              <FaLink />
              <span>Invitación</span>
            </button>
          </div>

          <div className="tab-content-wrapper">
            {/* Manual Code Entry Tab */}
            {activeTab === 'code' && (
              <div className="tab-content-container">
                <p className="tab-description">
                  Ingresa el código único del cliente para agregarlo a tu restaurante.
                  El formato es: <strong>XXXX-123</strong>
                </p>

                <Form onSubmit={handleSubmitCode}>
                  <Form.Group className="mb-3">
                    <Form.Label>Código del Cliente</Form.Label>
                    <Form.Control
                      type="text"
                      value={clientCode}
                      onChange={(e) => setClientCode(e.target.value.toUpperCase())}
                      placeholder="Ej: ABCD-123"
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      El cliente puede encontrar su código en su perfil
                    </Form.Text>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading || !clientCode.trim()}
                    className="w-100 submit-button"
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Agregando...
                      </>
                    ) : (
                      <>
                        <FaUserPlus /> Agregar Cliente
                      </>
                    )}
                  </Button>
                </Form>
              </div>
            )}

            {/* QR Code Scan Tab */}
            {activeTab === 'qr' && (
              <div className="tab-content-container">
                <p className="tab-description">
                  El cliente puede mostrar su código QR desde su perfil. Escanéalo para agregarlo automáticamente.
                </p>

                {!showQRScanner ? (
                  <div className="qr-scan-placeholder">
                    <FaQrcode className="qr-icon" />
                    <p>Escanear código QR del cliente</p>
                    <Button
                      variant="primary"
                      onClick={() => setShowQRScanner(true)}
                      className="scan-button"
                      disabled={loading}
                    >
                      <FaQrcode /> Iniciar escáner
                    </Button>
                  </div>
                ) : (
                  <div className="qr-scanner-container">
                    <QRScanner
                      isOpen={showQRScanner}
                      setIsOpen={setShowQRScanner}
                      setQRContent={handleQRCodeScanned}
                      setQrInput={setQrInput}
                    />
                    <Button
                      variant="secondary"
                      onClick={() => setShowQRScanner(false)}
                      className="cancel-scan-button"
                    >
                      Cancelar
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Invitation Link Tab */}
            {activeTab === 'invite' && (
              <div className="tab-content-container">
                <p className="tab-description">
                  Comparte este enlace o código QR con tus clientes para que se registren en tu restaurante.
                </p>

                {/* QR Code */}
                <div className="qr-code-container">
                  <QRCode value={invitationLink} size={200} />
                </div>

                {/* Invitation Link */}
                <div className="invitation-link-container">
                  <Form.Label>Enlace de Invitación</Form.Label>
                  <div className="link-input-group">
                    <Form.Control
                      type="text"
                      value={invitationLink}
                      readOnly
                      className="link-input"
                    />
                    <Button
                      variant="outline-primary"
                      onClick={handleCopyLink}
                      className="copy-button"
                    >
                      {copied ? (
                        <>
                          <FaCheckCircle /> Copiado
                        </>
                      ) : (
                        <>
                          <FaCopy /> Copiar
                        </>
                      )}
                    </Button>
                  </div>
                  <Form.Text className="text-muted">
                    Comparte este enlace por WhatsApp, email o redes sociales
                  </Form.Text>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AddClient;
